import { h, Fragment } from "preact";
import { Text } from "preact-i18n";
import styles from "./style.module.scss";
import StreetSelectionRow from "./StreetSelectionRow";
import SelectionWarnings from "./SelectionWarnings";
import type MvSelectedGroup from "models/MvSelectedGroup";
import { getAddressCount } from "services/selectionHelpers";
import type { AddressGroup, SelectionSummary } from "services/selectionGroups";
interface Props {
    group: AddressGroup;
    zipCodeAreaKey: string;
    isEditable: boolean;
    showFullZipCode: boolean;
    selectedGroups: MvSelectedGroup[];
    summary: SelectionSummary;
    zipCodeAreaMinCount?: number;
    toggleCheckAll(id: string, postcode: string, checked: boolean): void;
    toggleGroup(
        id: string,
        postcode: string,
        street: string,
        checked: boolean,
    ): void;
}
const PostcodeGroup = ({
    group,
    zipCodeAreaKey,
    isEditable,
    showFullZipCode,
    selectedGroups,
    summary,
    zipCodeAreaMinCount,
    toggleCheckAll,
    toggleGroup,
}: Props) => {
    const {
        isAllChecked,
        getZipCodeAreaTotal,
        getZipCodeAreaSelectedTotal,
        hasNotReachedSelectionMinimum,
        selectionDidNotReachSelectionMinimum,
    } = summary;
    const postcode = group.result?.postcode || "";
    const postCodeLabel = showFullZipCode ? (
        <>
            <b>{String(postcode).substring(0, 2)}</b>
            {String(postcode).substring(2)}
        </>
    ) : (
        zipCodeAreaKey
    );
    const streets = group.groupedByZipCodeAndStreetName[zipCodeAreaKey];
    const streetNames = Object.keys(streets);
    const selected =
        selectedGroups.find((selection) => selection.id === group.id)
            ?.selectedZipCodeByStreetName[zipCodeAreaKey] ?? [];
    const initialBelowMinimum = hasNotReachedSelectionMinimum(zipCodeAreaKey);
    const selectedBelowMinimum =
        selectionDidNotReachSelectionMinimum(zipCodeAreaKey);
    return (
        <Fragment>
            <table>
                <thead>
                    {streetNames.length > 0 && (
                        <tr>
                            {isEditable && (
                                <th>
                                    <input
                                        type="checkbox"
                                        id={`selectAll-${group.id}-${zipCodeAreaKey}`}
                                        name={`selectAll-${group.id}-${zipCodeAreaKey}`}
                                        checked={isAllChecked(
                                            group.id,
                                            zipCodeAreaKey,
                                        )}
                                        onChange={(e) =>
                                            toggleCheckAll(
                                                group.id,
                                                zipCodeAreaKey,
                                                (e.target as HTMLInputElement)
                                                    .checked,
                                            )
                                        }
                                    />
                                </th>
                            )}
                            <th className={styles.zipcodeArea}>
                                <Text id="slideInPanel.tableColumnLabels.zipCodeArea">
                                    Zipcode area
                                </Text>
                            </th>
                            <th>
                                <Text id="slideInPanel.tableColumnLabels.streetName">
                                    Street name
                                </Text>
                            </th>
                            <th>
                                <Text id="slideInPanel.tableColumnLabels.amountName">
                                    Amount
                                </Text>
                            </th>
                        </tr>
                    )}
                </thead>
                <tbody>
                    {streetNames.length > 0 ? (
                        <Fragment>
                            {streetNames
                                .sort((a, b) => a.localeCompare(b))
                                .map((street) => (
                                    <StreetSelectionRow
                                        key={street}
                                        areaId={group.id}
                                        street={street}
                                        postCodeLabel={postCodeLabel}
                                        isEditable={isEditable}
                                        selected={selected.includes(street)}
                                        count={getAddressCount(streets[street])}
                                        onToggle={(checked) =>
                                            toggleGroup(
                                                group.id,
                                                zipCodeAreaKey,
                                                street,
                                                checked,
                                            )
                                        }
                                    />
                                ))}
                            <tr>
                                {isEditable && (
                                    <td style={{ minWidth: "22px" }}></td>
                                )}
                                <td
                                    className={styles.zipcodeArea}
                                    style={{
                                        color: initialBelowMinimum
                                            ? "red"
                                            : "inherit",
                                    }}
                                >
                                    <strong>
                                        <Text id="general.messages.total">
                                            Total
                                        </Text>
                                    </strong>
                                </td>
                                <td
                                    style={{
                                        color:
                                            initialBelowMinimum ||
                                            selectedBelowMinimum
                                                ? "red"
                                                : "inherit",
                                    }}
                                >
                                    <SelectionWarnings
                                        zipCodeAreaMinCount={
                                            zipCodeAreaMinCount
                                        }
                                        initialBelowMinimum={
                                            initialBelowMinimum
                                        }
                                        selectedBelowMinimum={
                                            selectedBelowMinimum
                                        }
                                    />
                                </td>
                                <td
                                    style={{
                                        color: initialBelowMinimum
                                            ? "red"
                                            : "inherit",
                                    }}
                                >
                                    <strong>
                                        {getZipCodeAreaTotal(
                                            group.id,
                                            zipCodeAreaKey,
                                        )}
                                    </strong>
                                </td>
                            </tr>
                        </Fragment>
                    ) : (
                        <tr>
                            {isEditable && <td></td>}
                            <td>
                                <Text
                                    id="general.messages.addressNotFound"
                                    plural={2}
                                >
                                    No addresses found
                                </Text>
                            </td>
                            <td></td>
                        </tr>
                    )}
                </tbody>
            </table>
            <span className={styles.selectedMessage}>
                <Text id="general.labels.selected">Selected</Text>:{" "}
                {getZipCodeAreaSelectedTotal(group.id, zipCodeAreaKey)}
            </span>
        </Fragment>
    );
};
export default PostcodeGroup;
